import React, { useState, useEffect } from 'react';
import { fetchOrganizationData, updateOrganizationData } from '../../supabaseClient';
import { checkOrganizationStripeStatus, createOrganizationStripeAccount, fetchTicketTypes, uploadLogo } from '../../apiClient';
import Spinner from '../common/Spinner';
import Toast from '../common/Toast';
import { FaCog, FaPalette, FaStripe, FaEdit, FaTimes, FaCheck, FaSync } from 'react-icons/fa';
import SkeletonLoader from '../common/SkeletonLoader';
import AddressAutocomplete from '../common/AddressAutocomplete';
import Modal from '../common/Modal';

const getStatusColor = (status) => {
  switch (status) {
    case 'complete':
      return 'text-green-600';
    case 'pending':
    case 'incomplete':
      return 'text-yellow-600';
    default:
      return 'text-red-600';
  }
};

const getButtonText = (status) => {
  switch (status) {
    case 'pending':
      return 'Check Status';
    case 'incomplete':
      return 'Finish Connection';
    default:
      return 'Connect with Stripe';
  }
};

const OrganizationSettingsTab = ({ organizationId }) => {
  //console.log('Organization ID:', organizationId);
  const [organization, setOrganization] = useState(null);
  const [isEditAccountModalOpen, setIsEditAccountModalOpen] = useState(false);
  const [isEditBrandingModalOpen, setIsEditBrandingModalOpen] = useState(false);
  const [accountData, setAccountData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    description: '',
    ticket_sale_type: '',
  });
  const [brandingData, setBrandingData] = useState({
    primary_color: '',
    secondary_color: '',
    logoURL: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [error, setError] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [isConnectingStripe, setIsConnectingStripe] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stripeAccountDetails, setStripeAccountDetails] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [ticketTypeMap, setTicketTypeMap] = useState({});

  useEffect(() => {
    const loadOrganizationData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchOrganizationData(organizationId);
        //console.log('Organization dataa:', data);

        setOrganization(data);
        setAccountData({
          company_name: data?.company_name,
          first_name: data?.first_name,
          last_name: data?.last_name,
          address: data?.address,
          contact_email: data?.contact_email,
          contact_phone: data?.contact_phone,
          description: data?.description,
          ticket_sale_type: data?.ticket_sale_type || '',
        });
        setBrandingData({
          primary_color: data?.primary_color || '#1f3a8a',
          secondary_color: data?.secondary_color || '#1c4ed8',
          logoURL: data?.logourl,
        });
        setStripeAccountId(data?.stripe_account_id);
        setStripeAccountStatus(data?.stripe_account_status);
      } catch (err) {
        console.error('Error loading organization:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchTicketTypesData = async () => {
      try {
        const { data, error } = await fetchTicketTypes();
          
        if (error) throw new Error(error);
        setTicketTypes(data || []);
        
        const mapping = {};
        (data || []).forEach(type => {
          mapping[type.id] = type.name;
        });
        setTicketTypeMap(mapping);
      } catch (err) {
        console.error('Error fetching ticket types:', err);
      }
    };

    loadOrganizationData();
    fetchTicketTypesData();
  }, [organizationId]);

  useEffect(() => {
    if (stripeAccountId) {
      checkDetailedStatus();
    }
  }, [stripeAccountId]);

  const openEditAccountModal = () => setIsEditAccountModalOpen(true);
  const closeEditAccountModal = () => setIsEditAccountModalOpen(false);

  const openEditBrandingModal = () => setIsEditBrandingModalOpen(true);
  const closeEditBrandingModal = () => setIsEditBrandingModalOpen(false);

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountData(prev => ({ ...prev, [name]: value }));
  };

  const handleBrandingChange = (e) => {
    const { name, value } = e.target;
    setBrandingData(prev => ({ ...prev, [name]: value }));
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setSelectedFileName(file.name);
    }
  };

  const handleUpdateAccount = async () => {
    try {
      const { data, error } = await updateOrganizationData(organizationId, accountData);
      if (error) throw error;

      setOrganization(prev => ({ ...prev, ...data }));
      setIsEditAccountModalOpen(false);
      showToast('Account information updated successfully!');
    } catch (err) {
      console.error('Error updating account:', err);
      showToast('Failed to update account information', 'error');
    }
  };

  const handleUpdateBranding = async () => {
    try {
      let logourl = brandingData.logoURL;

      if (logoFile) {
        setIsUploading(true);
        const { data: uploadResult, error: uploadError } = await uploadLogo(logoFile, 'organization', organizationId);

        if (uploadError) throw new Error(uploadError);

        logourl = uploadResult.publicUrl;
      }

      const { error } = await updateOrganizationData(organizationId, {
        primary_color: brandingData.primary_color,
        secondary_color: brandingData.secondary_color,
        logourl,
      });

      if (error) throw error;

      setOrganization(prev => ({ 
        ...prev, 
        primary_color: brandingData.primary_color,
        secondary_color: brandingData.secondary_color,
        logourl,
      }));
      setIsEditBrandingModalOpen(false);
      showToast('Branding updated successfully!');
    } catch (err) {
      console.error('Error updating branding:', err);
      showToast('Failed to update branding', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleStripeConnect = async () => {
    setIsConnectingStripe(true);
    try {
      // if (stripeAccountStatus === 'incomplete') {
      //   const { accountLink } = await checkStripeAccountStatus(stripeAccountId);
  
      //   if (accountLink) {
      //     window.location.href = accountLink;
      //     return;
      //   }
      // }
      //console.log('Stripe Connect');
  
      const { data, error } = await createOrganizationStripeAccount(
        organizationId,
        organization.contact_email,
        organization.company_name
      );

      if (error) throw new Error(error);

      const { url, status } = data || {};

      if (status === 'complete') {
        setStripeAccountStatus('complete');
        showToast('Stripe account is already complete and active!', 'success');
      } else if (url) {
        window.location.href = url;
      } else {
        throw new Error('Failed to connect with Stripe');
      }
    } catch (error) {
      console.error('Error connecting to Stripe:', error);
      showToast('Failed to connect Stripe account', 'error');
    } finally {
      setIsConnectingStripe(false);
    }
  };

  const checkDetailedStatus = async () => {
    try {
      setIsConnectingStripe(true);
      if (!stripeAccountId) return;
  
      const { data: status, error: statusError } = await checkOrganizationStripeStatus(organizationId);
      if (statusError) throw statusError;
      
      setStripeAccountDetails(status);
      
      const newStatus = status.isComplete ? 'complete' : 'incomplete';
      setStripeAccountStatus(newStatus);

      if (status.isComplete) {
        showToast('Stripe account setup is complete!', 'success');
      } else if (status.accountLink) {
        showToast('Incomplete setup. Follow the link to complete the process.', 'warning');
      }
    } catch (error) {
      console.error('Error checking status:', error);
      showToast('Failed to check account status', 'error');
    } finally {
      setIsConnectingStripe(false);
    }
  };

  const InfoField = ({ label, value }) => (
    <div>
      <label className="block text-sm font-medium text-gray-500">{label}</label>
      <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md">
        <span className="text-gray-700">{value || 'Not set'}</span>
      </div>
    </div>
  );

  const AccountDetailsSkeleton = () => (
    <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-500 to-blue-700 p-4 flex justify-between items-center">
        <div className="flex items-center text-white">
          <FaCog className="text-2xl mr-3" />
          <h3 className="text-xl font-bold">Account Details</h3>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(9)].map((_, index) => (
            <div key={index} className="space-y-2">
              <div className="h-4 bg-gray-200 rounded-full w-24 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded-full w-48 animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const BrandingSkeleton = () => (
    <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
      <div className="bg-gradient-to-r from-purple-500 to-purple-700 p-4 flex justify-between items-center">
        <div className="flex items-center text-white">
          <FaPalette className="text-2xl mr-3" />
          <h3 className="text-xl font-bold">Branding</h3>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(2)].map((_, index) => (
            <div key={index}>
              <div className="h-4 bg-gray-200 rounded-full w-24 mb-2 animate-pulse"></div>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded-full w-20 ml-3 animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <div className="h-4 bg-gray-200 rounded-full w-16 mb-2 animate-pulse"></div>
          <div className="h-20 w-20 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
      </div>
    </div>
  );

  const StripeSkeleton = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 p-4">
        <div className="flex items-center text-white">
          <FaStripe className="text-2xl mr-3" />
          <h3 className="text-xl font-bold">Payment Processing</h3>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          <SkeletonLoader />
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <AccountDetailsSkeleton />
        <BrandingSkeleton />
        <StripeSkeleton />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {toast.show && <Toast message={toast.message} type={toast.type} />}

      {/* Account Information Card */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-blue-700 p-4 flex justify-between items-center">
          <div className="flex items-center text-white">
            <FaCog className="text-2xl mr-3" />
            <h3 className="text-xl font-bold">Organization Information</h3>
          </div>
          <button
            onClick={() => setIsEditAccountModalOpen(true)}
            className="p-2 text-white hover:bg-blue-600 rounded-full transition-colors duration-200"
          >
            <FaEdit className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoField label="Organization Name" value={organization?.company_name} />
            <InfoField label="First Name" value={organization?.first_name} />
            <InfoField label="Last Name" value={organization?.last_name} />
            <InfoField label="Email" value={organization?.contact_email} />
            <InfoField label="Phone" value={organization?.contact_phone} />
            <InfoField label="Address" value={organization?.address} />
            <InfoField label="Ticket Type" value={
              organization?.ticket_sale_type ? 
              ticketTypeMap[organization.ticket_sale_type] || 'Not set' : 
              'Not set'
            } />
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Convenience Fee
              </label>
              <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <span className="text-gray-700">{`$${organization?.convenience_fee || '0'} per ticket`}</span>
                <p className="mt-1 text-sm text-gray-500">
                  This fee is applied to all ticket sales for vendors in your organization
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Branding Card */}
      <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-500 to-purple-700 p-4 flex justify-between items-center">
          <div className="flex items-center text-white">
            <FaPalette className="text-2xl mr-3" />
            <h3 className="text-xl font-bold">Branding</h3>
          </div>
          <button
            onClick={() => setIsEditBrandingModalOpen(true)}
            className="p-2 text-white hover:bg-purple-600 rounded-full transition-colors duration-200"
          >
            <FaEdit className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Primary Color</label>
              <div className="flex items-center">
                <div
                  className="w-10 h-10 rounded-lg shadow-inner"
                  style={{ backgroundColor: organization?.primary_color }}
                />
                <span className="ml-3 font-medium">{organization?.primary_color}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">Secondary Color</label>
              <div className="flex items-center">
                <div
                  className="w-10 h-10 rounded-lg shadow-inner"
                  style={{ backgroundColor: organization?.secondary_color }}
                />
                <span className="ml-3 font-medium">{organization?.secondary_color}</span>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-500 mb-2">Logo</label>
            {organization?.logourl ? (
              <img src={organization.logourl} alt="Organization Logo" className="h-20 object-contain" />
            ) : (
              <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-400">No logo</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stripe Integration Card */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 p-4">
          <div className="flex items-center text-white">
            <FaStripe className="text-2xl mr-3" />
            <h3 className="text-xl font-bold">Payment Processing</h3>
          </div>
        </div>
        <div className="p-6">
          {!stripeAccountId ? (
            <div>
              <p className="text-gray-600 mb-4">Connect with Stripe to start accepting payments for your tickets.</p>
              <button
                onClick={handleStripeConnect}
                disabled={isConnectingStripe}
                className={`px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 flex items-center ${
                  isConnectingStripe ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isConnectingStripe ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Connecting to Stripe...
                  </>
                ) : (
                  <>
                    <FaStripe className="mr-2" />
                    Connect with Stripe
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Account Status */}
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-lg text-gray-700">Account Status</h4>
                  <button
                    onClick={checkDetailedStatus}
                    disabled={isConnectingStripe}
                    className={`p-2 text-gray-600 hover:text-blue-600 transition-colors duration-200 ${
                      isConnectingStripe ? 'animate-spin' : ''
                    }`}
                    title="Refresh Status"
                  >
                    <FaSync className="w-5 h-5" />
                  </button>
                </div>
                {stripeAccountDetails ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          stripeAccountDetails.detailsSubmitted ? 'bg-green-500' : 'bg-red-500'
                        }`}
                      />
                      <span className="text-gray-600">
                        Account Details: <strong>{stripeAccountDetails.detailsSubmitted ? 'Submitted' : 'Incomplete'}</strong>
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          stripeAccountDetails.chargesEnabled ? 'bg-green-500' : 'bg-red-500'
                        }`}
                      />
                      <span className="text-gray-600">
                        Charges: <strong>{stripeAccountDetails.chargesEnabled ? 'Enabled' : 'Disabled'}</strong>
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          stripeAccountDetails.payoutsEnabled ? 'bg-green-500' : 'bg-red-500'
                        }`}
                      />
                      <span className="text-gray-600">
                        Payouts: <strong>{stripeAccountDetails.payoutsEnabled ? 'Enabled' : 'Disabled'}</strong>
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">Loading status...</p>
                )}
              </div>

              {/* Required Actions Section */}
              {stripeAccountDetails?.currentlyDue?.length > 0 && (
                <div className="bg-yellow-50 p-4 rounded-lg shadow-sm">
                  <h4 className="font-semibold text-lg text-yellow-800 mb-2">Required Actions</h4>
                  <ul className="list-disc pl-5 text-yellow-700 mb-4">
                    {stripeAccountDetails.currentlyDue.map((requirement, index) => (
                      <li key={index}>{requirement.replace(/_/g, ' ').toLowerCase()}</li>
                    ))}
                  </ul>
                  <button
                    onClick={() => {
                      setIsRedirecting(true);
                      window.location.href = stripeAccountDetails.accountLink;
                    }}
                    disabled={isRedirecting}
                    className={`block w-full text-center px-4 py-2 rounded-lg transition-colors duration-200 shadow ${
                      isRedirecting
                        ? 'bg-yellow-400 text-white cursor-not-allowed opacity-75'
                        : 'bg-yellow-600 text-white hover:bg-yellow-700'
                    }`}
                  >
                    {isRedirecting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 inline-block mr-2"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Redirecting...
                      </>
                    ) : (
                      'Complete Missing Information'
                    )}
                  </button>
                </div>
              )}

              {/* Complete Setup Button */}
              {!stripeAccountDetails?.isComplete && (
                <button
                  onClick={handleStripeConnect}
                  disabled={isConnectingStripe}
                  className={`w-full mt-4 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 shadow-lg flex items-center justify-center ${
                    isConnectingStripe ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  {isConnectingStripe ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Connecting to Stripe...
                    </>
                  ) : (
                    'Complete Setup'
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Account Modal */}
      <Modal
        isOpen={isEditAccountModalOpen}
        onClose={closeEditAccountModal}
        title="Edit Organization Information"
      >
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization Name
              </label>
              <input
                type="text"
                name="company_name"
                value={accountData.company_name}
                onChange={handleAccountChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                name="first_name"
                value={accountData.first_name}
                onChange={handleAccountChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                value={accountData.last_name}
                onChange={handleAccountChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                name="contact_email"
                value={accountData.contact_email}
                onChange={handleAccountChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone
              </label>
              <input
                type="tel"
                name="contact_phone"
                value={accountData.contact_phone}
                onChange={handleAccountChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <AddressAutocomplete
                value={accountData.address}
                onChange={(value) => setAccountData(prev => ({ ...prev, address: value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ticket Type
              </label>
              <select
                name="ticket_sale_type"
                value={accountData.ticket_sale_type}
                onChange={handleAccountChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Ticket Type</option>
                {ticketTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={accountData.description}
                onChange={handleAccountChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-4">
          <button
            onClick={closeEditAccountModal}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateAccount}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Save Changes
          </button>
        </div>
      </Modal>

      {/* Edit Branding Modal */}
      <Modal
        isOpen={isEditBrandingModalOpen}
        onClose={closeEditBrandingModal}
        title="Edit Branding"
      >
        <div className="p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  name="primary_color"
                  value={brandingData.primary_color}
                  onChange={handleBrandingChange}
                  className="h-10 w-10 border-0 p-0"
                />
                <input
                  type="text"
                  name="primary_color"
                  value={brandingData.primary_color}
                  onChange={handleBrandingChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secondary Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  name="secondary_color"
                  value={brandingData.secondary_color}
                  onChange={handleBrandingChange}
                  className="h-10 w-10 border-0 p-0"
                />
                <input
                  type="text"
                  name="secondary_color"
                  value={brandingData.secondary_color}
                  onChange={handleBrandingChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logo
              </label>
              {brandingData.logoURL && (
                <div className="mb-4">
                  <img
                    src={brandingData.logoURL}
                    alt="Current Logo"
                    className="h-20 w-20 object-contain border rounded-lg"
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="cursor-pointer inline-block px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Choose New Logo
              </label>
              {selectedFileName && (
                <p className="mt-2 text-sm text-gray-500">{selectedFileName}</p>
              )}
            </div>
          </div>
        </div>
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-4">
          <button
            onClick={closeEditBrandingModal}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateBranding}
            disabled={isUploading}
            className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
              isUploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isUploading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizationSettingsTab;
